import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  Banknote,
  BarChart3,
  CalendarClock,
  CheckCircle2,
  Clock3,
  CreditCard,
  Download,
  Eye,
  FileCheck2,
  FileText,
  Filter,
  Landmark,
  Link2,
  MailCheck,
  Paperclip,
  Plus,
  ReceiptText,
  RefreshCcw,
  Search,
  Send,
  ShieldAlert,
  WalletCards,
  X,
} from "lucide-react";

import TitleButtons from "../../../../components/TitleButtons";
import { useTheme } from "../../../../context/ThemeContext";

const DEPARTMENT_COLOR = "#2f3f59";

type Tone = "neutral" | "success" | "warning" | "danger" | "info";
type StatusTone = "regular" | "aberto" | "vencido" | "pendente" | "concluido" | "cancelado";

type FinanceItem = {
  id: number;
  codigo: string;
  titulo: string;
  entidade: string;
  documento: string;
  competencia: string;
  emissao: string;
  vencimento: string;
  valor: number;
  status: StatusTone;
  categoria: string;
  centroCusto: string;
  responsavel: string;
  origem: string;
  observacao: string;
  historico: Array<{
    id: number;
    data: string;
    titulo: string;
    descricao: string;
    responsavel: string;
  }>;
};

type FilterState = {
  busca: string;
  competencia: string;
  status: string;
  categoria: string;
  entidade: string;
};

type LayoutProps = {
  section?: string;
};

function normalizeTextSearch(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];
  const clean = hex.replace("#", "").trim();
  if (clean.length !== 6) return [47, 63, 89];
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return [47, 63, 89];
  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatCurrency(value: number | null) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);
}

function formatDate(value: string | null) {
  if (!value) return "Não informado";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function formatCompetencia(value: string) {
  const [year, month] = value.split("-");
  if (!year || !month) return value;
  return `${month}/${year}`;
}

function getStatusLabel(status: StatusTone) {
  switch (status) {
    case "regular": return "Regular";
    case "aberto": return "Em aberto";
    case "vencido": return "Vencido";
    case "pendente": return "Pendente";
    case "concluido": return "Concluído";
    case "cancelado": return "Cancelado";
    default: return "Indefinido";
  }
}

function getStatusVisual(status: StatusTone) {
  switch (status) {
    case "regular":
    case "concluido":
      return { background: "#ecfdf3", color: "#166534", border: "#bbf7d0", icon: <CheckCircle2 size={12} /> };
    case "aberto":
      return { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", icon: <WalletCards size={12} /> };
    case "pendente":
      return { background: "#fff7ed", color: "#9a3412", border: "#fed7aa", icon: <Clock3 size={12} /> };
    case "vencido":
      return { background: "#fef2f2", color: "#991b1b", border: "#fecaca", icon: <AlertTriangle size={12} /> };
    case "cancelado":
      return { background: "#f8fafc", color: "#64748b", border: "#cbd5e1", icon: <ShieldAlert size={12} /> };
    default:
      return { background: "#f8fafc", color: "#64748b", border: "#cbd5e1", icon: <Clock3 size={12} /> };
  }
}

function getSectionTitle(section: string | undefined) {
  switch (section) {
    case "abertos": return "Em aberto";
    case "vencidos": return "Vencidos";
    case "pagos": return "Pagos";
    case "recebidos": return "Recebidos";
    case "pendencias": return "Pendências";
    case "historico": return "Histórico";
    case "relatorios": return "Relatórios";
    case "visao-geral":
    default: return "Visão geral";
  }
}

const Badge: React.FC<{ children: React.ReactNode; background: string; color: string; border: string; icon?: React.ReactNode }> = ({ children, background, color, border, icon }) => (
  <span style={{ ...styles.badge, background, color, borderColor: border }}>{icon}{children}</span>
);

const SummaryCard: React.FC<{ label: string; value: string | number; helper: string; tone: Tone; icon: React.ReactNode }> = ({ label, value, helper, tone, icon }) => {
  const iconStyle = tone === "success" ? styles.summaryIconOk : tone === "warning" ? styles.summaryIconWarning : tone === "danger" ? styles.summaryIconDanger : tone === "info" ? styles.summaryIconInfo : styles.summaryIcon;
  return (
    <div style={styles.summaryCard}>
      <div style={iconStyle}>{icon}</div>
      <div style={styles.summaryInfo}>
        <span style={styles.summaryLabel}>{label}</span>
        <strong style={styles.summaryValue}>{value}</strong>
        <span style={styles.summaryHelper}>{helper}</span>
      </div>
    </div>
  );
};

const FinanceRow: React.FC<{ item: FinanceItem; departmentColor: string; onOpen: (item: FinanceItem) => void }> = ({ item, departmentColor, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  const visual = getStatusVisual(item.status);

  return (
    <tr
      style={{ ...styles.tr, background: hovered ? toRgba(departmentColor, 0.05) : "#ffffff", boxShadow: hovered ? `inset 3px 0 0 ${departmentColor}` : "inset 0 0 0 transparent" }}
      title="Duplo clique para abrir o detalhamento"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => onOpen(item)}
    >
      <td style={styles.td}>
        <div style={styles.primaryCell}>
          <div style={{ ...styles.rowIcon, color: hovered ? departmentColor : "#475569", borderColor: hovered ? toRgba(departmentColor, 0.3) : "#dbe4ef", background: hovered ? toRgba(departmentColor, 0.07) : "#f8fafc" }}>
            <ReceiptText size={15} />
          </div>
          <div>
            <strong style={{ ...styles.mainStrong, color: hovered ? departmentColor : "#111827" }}>{item.codigo}</strong>
            <span style={styles.mutedText}>{item.titulo}</span>
          </div>
        </div>
      </td>
      <td style={styles.td}>
        <div style={styles.mainText}>{item.entidade}</div>
        <div style={styles.mutedText}>{item.documento}</div>
      </td>
      <td style={styles.td}>
        <div style={styles.mainText}>{formatDate(item.vencimento)}</div>
        <div style={styles.mutedText}>Competência {formatCompetencia(item.competencia)}</div>
      </td>
      <td style={styles.td}>
        <div style={styles.valueText}>{formatCurrency(item.valor)}</div>
        <div style={styles.mutedText}>{item.categoria}</div>
      </td>
      <td style={styles.td}>
        <div style={styles.mainText}>{item.centroCusto}</div>
        <div style={styles.mutedText}>{item.origem}</div>
      </td>
      <td style={styles.tdNoRightBorder}>
        <Badge background={visual.background} color={visual.color} border={visual.border} icon={visual.icon}>{getStatusLabel(item.status)}</Badge>
      </td>
    </tr>
  );
};

const DetailOverlay: React.FC<{ item: FinanceItem | null; departmentColor: string; moduleTitle: string; onClose: () => void }> = ({ item, departmentColor, moduleTitle, onClose }) => {
  if (!item) return null;
  const visual = getStatusVisual(item.status);
  return (
    <div style={styles.overlayBackdrop}>
      <div style={styles.overlayPanel}>
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>Financeiro / {moduleTitle}</div>
            <h2 style={styles.overlayTitle}>{item.codigo} · {item.entidade}</h2>
            <p style={styles.overlaySubtitle}>Detalhamento financeiro, vínculo documental, responsável, origem do lançamento e histórico operacional.</p>
          </div>
          <button type="button" style={styles.overlayCloseButton} onClick={onClose} aria-label="Fechar detalhamento"><X size={17} /></button>
        </div>

        <div style={styles.detailContent}>
          <section style={styles.detailTopGrid}>
            <div style={styles.detailCard}><span style={styles.detailLabel}>Valor</span><strong style={styles.detailValue}>{formatCurrency(item.valor)}</strong><span style={styles.detailMeta}>{item.categoria}</span></div>
            <div style={styles.detailCard}><span style={styles.detailLabel}>Vencimento</span><strong style={styles.detailValue}>{formatDate(item.vencimento)}</strong><span style={styles.detailMeta}>Competência {formatCompetencia(item.competencia)}</span></div>
            <div style={styles.detailCard}><span style={styles.detailLabel}>Status</span><div style={styles.detailBadgeArea}><Badge background={visual.background} color={visual.color} border={visual.border} icon={visual.icon}>{getStatusLabel(item.status)}</Badge></div></div>
            <div style={styles.detailCard}><span style={styles.detailLabel}>Responsável</span><strong style={styles.detailValueSmall}>{item.responsavel}</strong><span style={styles.detailMeta}>{item.centroCusto}</span></div>
          </section>

          <section style={styles.detailGridTwo}>
            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}><FileText size={15} /><strong>Dados principais</strong></div>
              <div style={styles.infoGridOne}>
                <div style={styles.infoItem}><span>Título</span><strong>{item.titulo}</strong></div>
                <div style={styles.infoItem}><span>Entidade</span><strong>{item.entidade}</strong></div>
                <div style={styles.infoItem}><span>Documento</span><strong>{item.documento}</strong></div>
                <div style={styles.infoItem}><span>Origem</span><strong>{item.origem}</strong></div>
              </div>
            </div>
            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}><Paperclip size={15} /><strong>Documentos e vínculos</strong></div>
              <div style={styles.documentsGridSingle}>
                <div style={styles.documentCard}>
                  <div style={styles.documentHeader}>
                    <div style={styles.documentTitleArea}><strong>Documento base</strong><span>{item.documento}</span></div>
                    <Badge background="#ecfdf3" color="#166534" border="#bbf7d0" icon={<CheckCircle2 size={12} />}>Vinculado</Badge>
                  </div>
                  <div style={styles.documentFooter}>
                    <span>Emissão em {formatDate(item.emissao)}</span>
                    <div style={styles.documentActions}>
                      <button type="button" style={{ ...styles.iconButton, color: departmentColor, borderColor: toRgba(departmentColor, 0.35) }}><Eye size={13} /></button>
                      <button type="button" style={{ ...styles.iconButton, color: departmentColor, borderColor: toRgba(departmentColor, 0.35) }}><Download size={13} /></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}><Clock3 size={15} /><strong>Histórico</strong></div>
            <div style={styles.historyList}>
              {item.historico.map((hist) => (
                <div key={hist.id} style={styles.historyItem}>
                  <div style={styles.historyDot} />
                  <div><strong>{hist.titulo}</strong><span>{formatDate(hist.data)} · {hist.responsavel}</span><p>{hist.descricao}</p></div>
                </div>
              ))}
            </div>
          </section>

          {item.observacao && <section style={styles.observationBox}><strong>Observação interna</strong><span>{item.observacao}</span></section>}
        </div>

        <div style={styles.overlayFooter}>
          <button type="button" style={styles.cancelButton} onClick={onClose}>Fechar</button>
          <button type="button" style={styles.secondaryButton}><RefreshCcw size={14} />Atualizar status</button>
          <button type="button" style={styles.secondaryButton}><Paperclip size={14} />Anexar documento</button>
          <button type="button" style={{ ...styles.primaryButton, background: departmentColor, borderColor: departmentColor }}><Send size={14} />Registrar ação</button>
        </div>
      </div>
    </div>
  );
};

const fixedItems: FinanceItem[] = [
  {
    "id": 1,
    "codigo": "CP-2026-0001",
    "titulo": "Fornecedor a pagar",
    "entidade": "Googlemarine Serviços Marítimos Ltda",
    "documento": "NFS-e 000000124",
    "competencia": "2026-05",
    "emissao": "2026-05-03",
    "vencimento": "2026-05-18",
    "valor": 12500,
    "status": "aberto",
    "categoria": "Fornecedor",
    "centroCusto": "Laboratório / Reparos",
    "responsavel": "Financeiro",
    "origem": "Nota fiscal",
    "observacao": "Registro demonstrativo para estruturar a tela antes da integração com API.",
    "historico": [
      {
        "id": 1,
        "data": "2026-05-03",
        "titulo": "Registro criado",
        "descricao": "Lançamento financeiro registrado no módulo.",
        "responsavel": "Financeiro"
      },
      {
        "id": 2,
        "data": "2026-05-18",
        "titulo": "Status atualizado",
        "descricao": "Movimentação acompanhada pelo responsável financeiro.",
        "responsavel": "Financeiro"
      }
    ]
  },
  {
    "id": 2,
    "codigo": "CP-2026-0002",
    "titulo": "Boleto operacional",
    "entidade": "Bravante Navegação S.A.",
    "documento": "Pedido 4501440911",
    "competencia": "2026-05",
    "emissao": "2026-05-05",
    "vencimento": "2026-05-20",
    "valor": 18750,
    "status": "pendente",
    "categoria": "Infraestrutura",
    "centroCusto": "Operações / Marítimo",
    "responsavel": "Guilherme Brito",
    "origem": "Pedido de compra",
    "observacao": "Registro demonstrativo para estruturar a tela antes da integração com API.",
    "historico": [
      {
        "id": 1,
        "data": "2026-05-05",
        "titulo": "Registro criado",
        "descricao": "Lançamento financeiro registrado no módulo.",
        "responsavel": "Financeiro"
      },
      {
        "id": 2,
        "data": "2026-05-20",
        "titulo": "Status atualizado",
        "descricao": "Movimentação acompanhada pelo responsável financeiro.",
        "responsavel": "Financeiro"
      }
    ]
  },
  {
    "id": 3,
    "codigo": "CP-2026-0003",
    "titulo": "Despesa administrativa",
    "entidade": "Marlin Navegação Ltda",
    "documento": "Boleto 10293",
    "competencia": "2026-05",
    "emissao": "2026-05-07",
    "vencimento": "2026-05-09",
    "valor": 9800,
    "status": "vencido",
    "categoria": "Administrativo",
    "centroCusto": "Tecnologia / Logvi",
    "responsavel": "Contas a pagar",
    "origem": "Contrato",
    "observacao": "Registro demonstrativo para estruturar a tela antes da integração com API.",
    "historico": [
      {
        "id": 1,
        "data": "2026-05-07",
        "titulo": "Registro criado",
        "descricao": "Lançamento financeiro registrado no módulo.",
        "responsavel": "Financeiro"
      },
      {
        "id": 2,
        "data": "2026-05-09",
        "titulo": "Status atualizado",
        "descricao": "Movimentação acompanhada pelo responsável financeiro.",
        "responsavel": "Financeiro"
      }
    ]
  },
  {
    "id": 4,
    "codigo": "CP-2026-0004",
    "titulo": "Pagamento de compra",
    "entidade": "OSM Thome Brasil",
    "documento": "Contrato LOGVI-2026",
    "competencia": "2026-05",
    "emissao": "2026-05-08",
    "vencimento": "2026-06-10",
    "valor": 6200,
    "status": "concluido",
    "categoria": "Fornecedor",
    "centroCusto": "Financeiro",
    "responsavel": "Contas a receber",
    "origem": "Portal do cliente",
    "observacao": "Registro demonstrativo para estruturar a tela antes da integração com API.",
    "historico": [
      {
        "id": 1,
        "data": "2026-05-08",
        "titulo": "Registro criado",
        "descricao": "Lançamento financeiro registrado no módulo.",
        "responsavel": "Financeiro"
      },
      {
        "id": 2,
        "data": "2026-06-10",
        "titulo": "Status atualizado",
        "descricao": "Movimentação acompanhada pelo responsável financeiro.",
        "responsavel": "Financeiro"
      }
    ]
  },
  {
    "id": 5,
    "codigo": "CP-2026-0005",
    "titulo": "Obrigação recorrente",
    "entidade": "Vision Marine Electronics",
    "documento": "NF compra 7712",
    "competencia": "2026-06",
    "emissao": "2026-06-03",
    "vencimento": "2026-06-20",
    "valor": 4500,
    "status": "aberto",
    "categoria": "Infraestrutura",
    "centroCusto": "Comercial / Propostas",
    "responsavel": "Diretoria",
    "origem": "Lançamento manual",
    "observacao": "Registro demonstrativo para estruturar a tela antes da integração com API.",
    "historico": [
      {
        "id": 1,
        "data": "2026-06-03",
        "titulo": "Registro criado",
        "descricao": "Lançamento financeiro registrado no módulo.",
        "responsavel": "Financeiro"
      },
      {
        "id": 2,
        "data": "2026-06-20",
        "titulo": "Status atualizado",
        "descricao": "Movimentação acompanhada pelo responsável financeiro.",
        "responsavel": "Financeiro"
      }
    ]
  }
];

const competenciasDisponiveis = Array.from(new Set(fixedItems.map((item) => item.competencia))).sort((a, b) => b.localeCompare(a));
const DEFAULT_COMPETENCIA = competenciasDisponiveis[0] || "";

const initialFilters: FilterState = {
  busca: "",
  competencia: DEFAULT_COMPETENCIA,
  status: "",
  categoria: "",
  entidade: "",
};

const ContasPagarLayout: React.FC<LayoutProps> = ({ section }) => {
  const { departmentColor } = useTheme();
  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;
  const activeSection = section || "visao-geral";
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [selectedItem, setSelectedItem] = useState<FinanceItem | null>(null);

  const selectedCompetencia = filters.competencia || DEFAULT_COMPETENCIA;

  const entidades = useMemo(() => {
    return Array.from(new Set(fixedItems.filter((item) => selectedCompetencia ? item.competencia === selectedCompetencia : true).map((item) => item.entidade))).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [selectedCompetencia]);

  const categorias = useMemo(() => {
    return Array.from(new Set(fixedItems.map((item) => item.categoria))).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, []);

  const filteredByMonth = useMemo(() => {
    if (!selectedCompetencia) return fixedItems;
    return fixedItems.filter((item) => item.competencia === selectedCompetencia);
  }, [selectedCompetencia]);

  const filteredBySection = useMemo(() => {
    switch (activeSection) {
      case "abertos": return filteredByMonth.filter((item) => item.status === "aberto");
      case "vencidos": return filteredByMonth.filter((item) => item.status === "vencido");
      case "pagos":
      case "recebidos": return filteredByMonth.filter((item) => item.status === "concluido" || item.status === "regular");
      case "pendencias": return filteredByMonth.filter((item) => item.status === "pendente" || item.status === "vencido");
      default: return filteredByMonth;
    }
  }, [activeSection, filteredByMonth]);

  const filteredItems = useMemo(() => {
    const busca = normalizeTextSearch(filters.busca);
    return filteredBySection.filter((item) => {
      const buscaMatch = !busca || [item.codigo, item.titulo, item.entidade, item.documento, item.categoria, item.centroCusto, item.responsavel, item.origem].some((field) => normalizeTextSearch(field).includes(busca));
      const statusMatch = !filters.status || item.status === filters.status;
      const categoriaMatch = !filters.categoria || item.categoria === filters.categoria;
      const entidadeMatch = !filters.entidade || item.entidade === filters.entidade;
      return buscaMatch && statusMatch && categoriaMatch && entidadeMatch;
    });
  }, [filteredBySection, filters]);

  const resumo = useMemo(() => {
    const total = filteredByMonth.reduce((sum, item) => sum + item.valor, 0);
    const aberto = filteredByMonth.filter((item) => item.status === "aberto" || item.status === "pendente").reduce((sum, item) => sum + item.valor, 0);
    const vencido = filteredByMonth.filter((item) => item.status === "vencido").reduce((sum, item) => sum + item.valor, 0);
    const concluido = filteredByMonth.filter((item) => item.status === "concluido" || item.status === "regular").reduce((sum, item) => sum + item.valor, 0);
    return { total, aberto, vencido, concluido, quantidade: filteredByMonth.length, pendentes: filteredByMonth.filter((item) => item.status === "pendente" || item.status === "vencido").length };
  }, [filteredByMonth]);

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ ...initialFilters, competencia: selectedCompetencia || DEFAULT_COMPETENCIA });
  };

  return (
    <div style={styles.page}>
      <TitleButtons fullBleedLine title={`Contas a pagar - ${getSectionTitle(activeSection)}`} />

      <div style={styles.content}>
        <section style={styles.monthPanel}>
          <div style={styles.monthInfo}>
            <strong>Apuração mensal</strong>
            <span>Controle de fornecedores, boletos, notas de compra, vencimentos, aprovações, comprovantes e baixas de pagamento.</span>
          </div>
          <div style={styles.monthControl}>
            <span>Competência</span>
            <select style={styles.monthSelect} value={selectedCompetencia} onChange={(event) => updateFilter("competencia", event.target.value)}>
              {competenciasDisponiveis.map((competencia) => <option key={competencia} value={competencia}>{formatCompetencia(competencia)}</option>)}
            </select>
          </div>
        </section>

        <section style={styles.summaryGrid}>
          <SummaryCard label="Obrigações no mês" value={resumo.quantidade} helper={`${resumo.pendentes} pendência(s) na competência`} tone="neutral" icon={<CreditCard size={16} />} />
          <SummaryCard label="Total a pagar" value={formatCurrency(resumo.aberto || resumo.total)} helper={`Competência ${formatCompetencia(selectedCompetencia)}`} tone="info" icon={<WalletCards size={16} />} />
          <SummaryCard label="Vencidas" value={formatCurrency(resumo.vencido)} helper="Itens vencidos ou com atenção" tone="warning" icon={<AlertTriangle size={16} />} />
          <SummaryCard label="Pagas/baixadas" value={formatCurrency(resumo.concluido)} helper="Itens concluídos/regularizados" tone="success" icon={<CheckCircle2 size={16} />} />
        </section>

        <section style={styles.actionBar}>
          <div style={styles.actionBarInfo}>
            <strong>Contas a Pagar</strong>
            <span>Controle de fornecedores, boletos, notas de compra, vencimentos, aprovações, comprovantes e baixas de pagamento.</span>
          </div>
          <div style={styles.actionButtons}>
            <button type="button" style={{ ...styles.primaryButton, background: activeDepartmentColor, borderColor: activeDepartmentColor }}><Plus size={14} />Novo registro</button>
            <button type="button" style={styles.secondaryButton}><Paperclip size={14} />Anexar documento</button>
            <button type="button" style={styles.secondaryButton}><Download size={14} />Exportar</button>
          </div>
        </section>

        <section style={styles.filterPanel}>
          <div style={styles.filterHeader}>
            <div style={styles.filterTitleArea}><Filter size={15} /><strong>Filtros da listagem</strong></div>
            <button type="button" style={styles.clearButton} onClick={clearFilters}>Limpar filtros</button>
          </div>
          <div style={styles.filterGrid}>
            <div style={styles.searchField}><Search size={14} /><input style={styles.searchInput} value={filters.busca} onChange={(event) => updateFilter("busca", event.target.value)} placeholder="Buscar por código, entidade, documento, categoria, centro de custo ou origem..." /></div>
            <select style={styles.filterInput} value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
              <option value="">Todos os status</option>
              <option value="aberto">Em aberto</option>
              <option value="pendente">Pendente</option>
              <option value="vencido">Vencido</option>
              <option value="concluido">Concluído</option>
              <option value="regular">Regular</option>
              <option value="cancelado">Cancelado</option>
            </select>
            <select style={styles.filterInput} value={filters.categoria} onChange={(event) => updateFilter("categoria", event.target.value)}>
              <option value="">Todas as categorias</option>
              {categorias.map((categoria) => <option key={categoria} value={categoria}>{categoria}</option>)}
            </select>
            <select style={styles.filterInput} value={filters.entidade} onChange={(event) => updateFilter("entidade", event.target.value)}>
              <option value="">Todas as entidades</option>
              {entidades.map((entidade) => <option key={entidade} value={entidade}>{entidade}</option>)}
            </select>
            <select style={styles.filterInput} value={filters.competencia} onChange={(event) => updateFilter("competencia", event.target.value)}>
              {competenciasDisponiveis.map((competencia) => <option key={competencia} value={competencia}>{formatCompetencia(competencia)}</option>)}
            </select>
          </div>
        </section>

        <section style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <div style={styles.tableTitleArea}>
              <strong>Listagem operacional</strong>
              <span>{filteredItems.length} registro(s) encontrado(s)</span>
            </div>
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Registro</th>
                  <th style={styles.th}>Entidade</th>
                  <th style={styles.th}>Vencimento</th>
                  <th style={styles.th}>Valor</th>
                  <th style={styles.th}>Centro de custo</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => <FinanceRow key={item.id} item={item} departmentColor={activeDepartmentColor} onOpen={setSelectedItem} />)}
              </tbody>
            </table>
            {filteredItems.length === 0 && <div style={styles.emptyState}>Nenhum registro encontrado para os filtros selecionados.</div>}
          </div>
        </section>
      </div>

      <DetailOverlay item={selectedItem} departmentColor={activeDepartmentColor} moduleTitle="Contas a Pagar" onClose={() => setSelectedItem(null)} />
    </div>
  );
};


const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: "#f4f7fb", color: "#0f172a" },
  content: { padding: "18px 24px 34px", display: "flex", flexDirection: "column", gap: 14 },
  monthPanel: { display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", padding: "14px 16px", background: "#ffffff", border: "1px solid #dbe4ef", borderRadius: 16, boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)" },
  monthInfo: { display: "flex", flexDirection: "column", gap: 4 },
  monthControl: { display: "flex", alignItems: "center", gap: 10, color: "#475569", fontSize: 12, fontWeight: 700 },
  monthSelect: { height: 36, border: "1px solid #cbd5e1", borderRadius: 10, padding: "0 12px", background: "#fff", color: "#0f172a", fontSize: 13, outline: "none" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 },
  summaryCard: { minHeight: 96, display: "flex", gap: 12, alignItems: "flex-start", background: "#ffffff", border: "1px solid #dbe4ef", borderRadius: 16, padding: 14, boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)" },
  summaryIcon: { width: 34, height: 34, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#334155", background: "#f1f5f9", border: "1px solid #e2e8f0" },
  summaryIconOk: { width: 34, height: 34, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#166534", background: "#ecfdf3", border: "1px solid #bbf7d0" },
  summaryIconWarning: { width: 34, height: 34, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#9a3412", background: "#fff7ed", border: "1px solid #fed7aa" },
  summaryIconDanger: { width: 34, height: 34, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#991b1b", background: "#fef2f2", border: "1px solid #fecaca" },
  summaryIconInfo: { width: 34, height: 34, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe" },
  summaryInfo: { display: "flex", flexDirection: "column", gap: 3, minWidth: 0 },
  summaryLabel: { fontSize: 12, color: "#64748b", fontWeight: 700 },
  summaryValue: { fontSize: 20, color: "#0f172a", lineHeight: 1.15 },
  summaryHelper: { fontSize: 11.5, color: "#64748b", lineHeight: 1.35 },
  actionBar: { display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", padding: "14px 16px", background: "#ffffff", border: "1px solid #dbe4ef", borderRadius: 16, boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)" },
  actionBarInfo: { display: "flex", flexDirection: "column", gap: 4 },
  actionButtons: { display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" },
  primaryButton: { height: 36, borderRadius: 10, border: "1px solid transparent", padding: "0 13px", color: "#ffffff", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  secondaryButton: { height: 36, borderRadius: 10, border: "1px solid #cbd5e1", padding: "0 12px", background: "#ffffff", color: "#334155", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  cancelButton: { height: 36, borderRadius: 10, border: "1px solid #cbd5e1", padding: "0 12px", background: "#f8fafc", color: "#334155", fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  filterPanel: { background: "#ffffff", border: "1px solid #dbe4ef", borderRadius: 16, padding: 14, boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)" },
  filterHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  filterTitleArea: { display: "flex", alignItems: "center", gap: 8, color: "#334155", fontSize: 13 },
  clearButton: { border: "none", background: "transparent", color: "#64748b", fontWeight: 800, fontSize: 12, cursor: "pointer" },
  filterGrid: { display: "grid", gridTemplateColumns: "2fr repeat(4, 1fr)", gap: 10 },
  searchField: { height: 38, border: "1px solid #cbd5e1", borderRadius: 11, display: "flex", alignItems: "center", gap: 8, padding: "0 11px", background: "#ffffff", color: "#64748b" },
  searchInput: { border: "none", outline: "none", width: "100%", fontSize: 13, color: "#0f172a" },
  filterInput: { height: 38, border: "1px solid #cbd5e1", borderRadius: 11, padding: "0 10px", background: "#ffffff", color: "#0f172a", fontSize: 12.5, outline: "none" },
  tableCard: { background: "#ffffff", border: "1px solid #dbe4ef", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)" },
  tableHeader: { display: "flex", justifyContent: "space-between", gap: 16, padding: "14px 16px", borderBottom: "1px solid #e2e8f0", alignItems: "center" },
  tableTitleArea: { display: "flex", flexDirection: "column", gap: 4 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 1040 },
  th: { textAlign: "left", padding: "11px 12px", fontSize: 11.5, color: "#64748b", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3 },
  tr: { transition: "background 120ms ease, box-shadow 120ms ease", cursor: "default" },
  td: { padding: "13px 12px", borderBottom: "1px solid #eef2f7", borderRight: "1px solid #f1f5f9", verticalAlign: "middle" },
  tdNoRightBorder: { padding: "13px 12px", borderBottom: "1px solid #eef2f7", verticalAlign: "middle" },
  primaryCell: { display: "flex", alignItems: "center", gap: 10 },
  rowIcon: { width: 32, height: 32, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbe4ef" },
  mainStrong: { display: "block", fontSize: 13.5, lineHeight: 1.25 },
  mainText: { fontSize: 13, color: "#111827", fontWeight: 700 },
  mutedText: { display: "block", marginTop: 3, fontSize: 11.5, color: "#64748b" },
  valueText: { fontSize: 13.5, color: "#0f172a", fontWeight: 900 },
  badge: { display: "inline-flex", alignItems: "center", gap: 5, border: "1px solid", borderRadius: 999, padding: "4px 8px", fontSize: 11.5, fontWeight: 900, whiteSpace: "nowrap" },
  emptyState: { padding: 28, textAlign: "center", color: "#64748b", fontSize: 13 },
  overlayBackdrop: { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.35)", backdropFilter: "blur(5px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  overlayPanel: { width: "min(1180px, 96vw)", maxHeight: "92vh", background: "#ffffff", borderRadius: 20, border: "1px solid #dbe4ef", boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)", display: "flex", flexDirection: "column", overflow: "hidden" },
  overlayHeader: { padding: "18px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", gap: 18 },
  overlayEyebrow: { fontSize: 11.5, color: "#64748b", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.4 },
  overlayTitle: { margin: "5px 0 4px", fontSize: 20, color: "#0f172a" },
  overlaySubtitle: { margin: 0, color: "#64748b", fontSize: 13, lineHeight: 1.45 },
  overlayCloseButton: { width: 34, height: 34, borderRadius: 11, border: "1px solid #cbd5e1", background: "#ffffff", color: "#334155", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  detailContent: { padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 },
  detailTopGrid: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 },
  detailCard: { border: "1px solid #e2e8f0", borderRadius: 14, background: "#f8fafc", padding: 13, display: "flex", flexDirection: "column", gap: 5, minHeight: 86 },
  detailLabel: { fontSize: 11.5, color: "#64748b", fontWeight: 900 },
  detailValue: { fontSize: 18, color: "#0f172a" },
  detailValueSmall: { fontSize: 14, color: "#0f172a" },
  detailMeta: { fontSize: 11.5, color: "#64748b" },
  detailBadgeArea: { marginTop: 6 },
  detailGridTwo: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  detailSection: { border: "1px solid #e2e8f0", borderRadius: 16, background: "#ffffff", padding: 14 },
  detailSectionHeader: { display: "flex", alignItems: "center", gap: 8, color: "#334155", marginBottom: 12, fontSize: 13.5 },
  infoGridOne: { display: "grid", gridTemplateColumns: "1fr", gap: 9 },
  infoItem: { background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 4 },
  documentsGridSingle: { display: "grid", gridTemplateColumns: "1fr", gap: 10 },
  documentCard: { border: "1px solid #e2e8f0", borderRadius: 14, padding: 12, background: "#ffffff" },
  documentHeader: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" },
  documentTitleArea: { display: "flex", flexDirection: "column", gap: 4, minWidth: 0 },
  documentFooter: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginTop: 12, color: "#64748b", fontSize: 11.5 },
  documentActions: { display: "flex", gap: 6 },
  iconButton: { width: 28, height: 28, borderRadius: 9, border: "1px solid #e2e8f0", background: "#ffffff", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  historyList: { display: "flex", flexDirection: "column", gap: 10 },
  historyItem: { display: "grid", gridTemplateColumns: "12px 1fr", gap: 9, color: "#334155" },
  historyDot: { width: 8, height: 8, borderRadius: 999, background: "#94a3b8", marginTop: 5 },
  observationBox: { border: "1px solid #fed7aa", background: "#fff7ed", color: "#9a3412", borderRadius: 14, padding: 12, display: "flex", flexDirection: "column", gap: 4, fontSize: 13 },
  overlayFooter: { padding: 14, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 8, background: "#f8fafc" },
};


export default ContasPagarLayout;
